import { signal, useSignalEffect } from '@preact/signals';
import { io } from 'socket.io-client';

import './style.scss';
import ChatHeaderChatbot from './components/chatHeaderChatbot.jsx';
import ChatInputFormChatbot from './components/chatInputFormChatbot.jsx';
import ChatBotNavChatbot from './components/chatBotNavChatbot.jsx';
import HistoryChatbot from './components/historyChatbot.jsx';
import { sendLocationChatbot } from './utils/location';
import { generateUuidChatbot } from './utils/uuid';

import {
  CHATBOT_CHAT_MSJ_FROM_BOT,
  CHATBOT_CHAT_MSJ_FROM_CLIENT,
  CHATBOT_DEFAULT_WELCOME,
  CHATBOT_ERROR_EVENT,
  CHATBOT_MODE_CHATBOT,
  CHATBOT_MODE_RECOMMENDER,
  CHATBOT_OUTSIDE_MESSAGE,
  CHATBOT_OUTSIDE_AUDIO,
  CHATBOT_SOCKET_CONNECT,
  CHATBOT_SOCKET_DISCONNECT,
  CHATBOT_SOCKET_MESSAGE,
  CHATBOT_TOKEN_EVENT,
  CHATBOT_TOKEN_STRING,
  CHATBOT_TOKEN_USER,
  CHATBOT_WELCOME_NAVIGATION,
  CHATBOT_WHATSAPP_MESSAGE
} from './utils/constants';
import { openCallChatbot } from './utils/openCall.js';
import { getFeatureFlagsChatbot } from './utils/getFeatureFlags.js';
import fallbackFeatureFlag from './assets/featureFlag.json';
import RecommenderSlide from './components/recommenderWelcomeChatbot.jsx';
import ScreenInputLinkedinChatbot from './components/screenInputLinkedinChatbot.jsx';
import {detectOSChatbot } from './utils/detectOsAndBrowser.js';
import { render } from 'preact';
import { getBotStatus } from './utils/getBotStatus.js';
import DropdownChatbot from './components/dropDownChatbot';
import { getChatbotEnable, getPortalId } from './utils/getChatbotEnable';
import clsx from 'clsx';

window.sessionStorage.removeItem(CHATBOT_TOKEN_STRING);
const isOnline = true;
let socketChatbot = null;
let socketChatbotUuid = generateUuidChatbot();
const connectedChatbotState = signal(false);
const isLoadingStateDictChatbot = signal({
  [CHATBOT_MODE_CHATBOT]: false,
  [CHATBOT_MODE_RECOMMENDER]: false,
});
const dictMessengersStateChatbot = signal({
  [CHATBOT_MODE_CHATBOT]: [],
  [CHATBOT_MODE_RECOMMENDER]: [],
});
const socketChatbotToken = signal(null);
const openChatbotCount = signal(0);
const isOpenChatbot = signal(false);
const inputValueStateChatbot = signal('');
const isFirstRenderChatbot = signal(true);
const configFeatureFlagChatbot = signal(fallbackFeatureFlag);
const navigationStateChatbot = signal(0);
const welcomeNavigationStateChatbot = signal(parseInt(localStorage.getItem(CHATBOT_WELCOME_NAVIGATION)) || 0);
const modeChatbot = signal('chatbot');
const detectedOsStateChatbot = signal('');
const chatbotStatusIsOnline = signal(true);
const recommenderStatusIsOnline = signal(true);
const portalIdChatbot = signal(null);
const chatbotEnableSignal = signal(null);
const urlContainsPanel = window.location.href.includes('/panel/');

const urlParams = new URLSearchParams(window.location.search);
const valueQueryParam = urlParams.get('chatbot-mode');
if (valueQueryParam === 'recommender') {
  navigationStateChatbot.value = 1;
  isOpenChatbot.value = true;
} else if (valueQueryParam === 'chatbot') {
  navigationStateChatbot.value = 0;
  isOpenChatbot.value = true;
}

export function App() {
  getBotStatus(chatbotStatusIsOnline, recommenderStatusIsOnline);
  const getSocketChatbot = (autoConnect = false) => {
    const token = window.sessionStorage.getItem(CHATBOT_TOKEN_STRING);
    const config = {
      // TODO: uncomment in future with feature multiple tab and clean conversation
      // query: { token: token, uuid: socketUuid },
      query: { token: isFirstRenderChatbot.value ? null : token, uuid: socketChatbotUuid },
      transports: ['websocket'],
      autoConnect: false
    };
    return io(import.meta.env.VITE_APP_SOCKET_URL, config);
  };

  const addAudioToListChatbot = ({ from, audio, typeMode = modeChatbot.value }, id = null) => {
    let currentMessageState = dictMessengersStateChatbot.value[typeMode];
    if (currentMessageState.length) {
      const lastElement = currentMessageState[currentMessageState.length - 1];
      if (lastElement.from === from && lastElement.audio === audio) {
        return;
      }
    }
    const newMessage = {
      id: id || -currentMessageState.length,
      from,
      message: null,
      audio,
      score: 0,
      createdAt: Date.now(),
    };
    currentMessageState = [...currentMessageState, newMessage];
    dictMessengersStateChatbot.value[typeMode] = currentMessageState;
    dictMessengersStateChatbot.value = { ...dictMessengersStateChatbot.value };
  };

  const addMessageToListChatbot = ({ from, message, typeMode = modeChatbot.value, audio = null }, id = null) => {
    // Prevent duplicated messages
    let currentMessageState = dictMessengersStateChatbot.value[typeMode];
    if (currentMessageState.length) {
      const lastElement = currentMessageState[currentMessageState.length - 1];
      if (lastElement.from === from && lastElement.message === message) {
        return;
      }
    }
    // add new messages
    const newMessage = {
      id: id || -currentMessageState.length,
      from,
      message,
      audio,
      score: 0,
      createdAt: Date.now(),
    };
    currentMessageState = [...currentMessageState, newMessage];
    dictMessengersStateChatbot.value[typeMode] = currentMessageState;
    dictMessengersStateChatbot.value = {...dictMessengersStateChatbot.value};};
  const getTokenUserFromCookieChatbot = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const trimmedCookie = cookie.trim();
      const index = trimmedCookie.indexOf('=');
      if (index > -1) {
        const name = trimmedCookie.substring(0, index);
        const value = trimmedCookie.substring(index + 1);
        if (name === CHATBOT_TOKEN_USER) {
          return decodeURIComponent(value);
        }
      }
    }
    return null;
  };
  const sendMessageChatbot = ({from, message, audio = null, typeMode = modeChatbot.value}) => {
    const tokenUser = getTokenUserFromCookieChatbot();

    if (from === CHATBOT_CHAT_MSJ_FROM_CLIENT) {
      const recommenderIsOffline = modeChatbot.value === CHATBOT_MODE_RECOMMENDER && !recommenderStatusIsOnline.value;
      const chatbotIsOffline = modeChatbot.value === CHATBOT_MODE_CHATBOT && !chatbotStatusIsOnline.value;
      if (!connectedChatbotState.value || chatbotIsOffline || recommenderIsOffline ) {
        addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message: CHATBOT_WHATSAPP_MESSAGE, typeMode: typeMode, audio });
        return;
      }
      if (dictMessengersStateChatbot.value[modeChatbot.value].length === 1) {
        socketChatbot.emit(CHATBOT_SOCKET_MESSAGE, {
          from: CHATBOT_CHAT_MSJ_FROM_BOT,
          message: dictMessengersStateChatbot.value[modeChatbot.value][0].message,
          url: window.location.href,
          mode: typeMode,
          audio: null,
          [CHATBOT_TOKEN_USER]: tokenUser,
          userPanel: urlContainsPanel,
          portal: portalIdChatbot.value,
        });

        addMessageToListChatbot({ from, message , audio});
        isLoadingStateDictChatbot.value[modeChatbot.value] = true;
        setTimeout(() => {
          socketChatbot.emit(CHATBOT_SOCKET_MESSAGE, {
            from: CHATBOT_CHAT_MSJ_FROM_CLIENT,
            message,
            url: window.location.href,
            mode: typeMode,
            audio: audio,
            [CHATBOT_TOKEN_USER]: tokenUser,
            userPanel: urlContainsPanel,
            portal: portalIdChatbot,
          });
        }, 2000);
        return;
      }

      socketChatbot.emit(CHATBOT_SOCKET_MESSAGE, {
        from: CHATBOT_CHAT_MSJ_FROM_CLIENT,
        message,
        url: window.location.href,
        mode: typeMode,
        audio: audio,
        [CHATBOT_TOKEN_USER]: tokenUser,
        userPanel: urlContainsPanel,
        portal: portalIdChatbot,
      });
      addMessageToListChatbot({ from, message , audio});
      isLoadingStateDictChatbot.value[modeChatbot.value] = true;
    } else {
      addMessageToListChatbot({ from, message, audio });
    }
  };

  useSignalEffect(() => {
    if (navigationStateChatbot.value === 0) {
      modeChatbot.value = CHATBOT_MODE_CHATBOT;
    }else if (navigationStateChatbot.value === 1) {
      modeChatbot.value = CHATBOT_MODE_RECOMMENDER;
    }
  });

  useSignalEffect(() => {
    if (!isOnline || !isOpenChatbot.value || openChatbotCount.value > 0) {
      if (openChatbotCount.value > 1){
        connectedChatbotState.value = true;
      }
      return;
    }
    const newSocketChatbot = getSocketChatbot();
    newSocketChatbot.on(CHATBOT_SOCKET_CONNECT, () => {
      const queryToken = socketChatbot.io.opts.query.token
      if (queryToken) {
        // TODO: convert functions in async functions and execute predicates with finally for edge cases
        getFeatureFlagsChatbot(queryToken, (response) => {
          response.recommender = true;
          configFeatureFlagChatbot.value = response;
          if (!response){
            connectedChatbotState.value = true;
            return;
          }
          const initialMessageChatbot = response.initialMessageChatbot || CHATBOT_DEFAULT_WELCOME;
          addMessageToListChatbot({from: 'bot', message: initialMessageChatbot, typeMode: CHATBOT_MODE_CHATBOT}, -1);
          connectedChatbotState.value = true;
        });
      }
    });

    newSocketChatbot.on(CHATBOT_OUTSIDE_MESSAGE, ({ message, id }, channel) => {
      if (!message) {
        console.error('The message is empty');
        return;
      }
      addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, typeMode: channel, message }, id);
    });

    newSocketChatbot.on(CHATBOT_OUTSIDE_AUDIO, ({ audio, id }, channel) => {
      addAudioToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, typeMode: channel, audio }, id);
    });
    newSocketChatbot.on(CHATBOT_ERROR_EVENT, ({ message }) => {
      console.error('Error received: ', message);
    });

    newSocketChatbot.on(CHATBOT_TOKEN_EVENT, ({ accessToken }) => {
      socketChatbotToken.value = accessToken;
      window.sessionStorage.setItem(CHATBOT_TOKEN_STRING, accessToken);
      socketChatbot.io.opts.query = {token: accessToken, uuid: socketChatbotUuid};
      newSocketChatbot.disconnect().connect();
      // send location in re-connect
    });

    newSocketChatbot.on(CHATBOT_SOCKET_MESSAGE, ({ message, id }, channel) => {
      if (!message) {
        console.error('The message is empty');
        return;
      }
      isLoadingStateDictChatbot.value[channel] = false;
      addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message, typeMode: channel }, id);
    });

    newSocketChatbot.on(CHATBOT_SOCKET_DISCONNECT, () => {
      connectedChatbotState.value = false;
      // TODO: create event with multiple tabs with a modal (use here)
    });

    newSocketChatbot.on('connect_error', (error) => {
      console.error('Error connecting to socket: ', error);
      addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message: CHATBOT_WHATSAPP_MESSAGE });
    });

    newSocketChatbot.connect();
    socketChatbot = newSocketChatbot;
    console.info('Socket connected!!');
  });


  const handlerClick = () => {
    isFirstRenderChatbot.value = false;
    isOpenChatbot.value = !isOpenChatbot.value;
    openChatbotCount.value += 1;
    detectedOsStateChatbot.value = detectOSChatbot();
    if (isOpenChatbot.value && (detectedOsStateChatbot.value === 'android' || detectedOsStateChatbot.value === 'ios')) {
      document.documentElement.classList.add('bot-utn-no-scroll');
      document.documentElement.classList.remove('bot-utn-scroll');
      document.body.classList.add('bot-utn-no-scroll');
      document.body.classList.remove('bot-utn-scroll');
    }
    if (openChatbotCount.value === 1 && isOpenChatbot.value) {
      openCallChatbot(socketChatbot.io.opts.query.uuid);
    }
  };

  const welcomeAction = (skip) => {
    if (welcomeNavigationStateChatbot.value < 2) {
      return;
    }
    let initialMessageRecommender;
    if (skip) {
      initialMessageRecommender = configFeatureFlagChatbot.value.initialMessageRecommenderSkip || CHATBOT_DEFAULT_WELCOME;
    } else {
      initialMessageRecommender = configFeatureFlagChatbot.value.initialMessageRecommender || CHATBOT_DEFAULT_WELCOME;
    }

    addMessageToListChatbot({ from: 'bot', message: initialMessageRecommender, typeMode: CHATBOT_MODE_RECOMMENDER }, -1);
  }

  const sendNavigationChange = (skip = false) => {
    // TODO: uncomment in future with feature multiple tab and clean conversation
    // const currentState = parseInt(localStorage.getItem(WELCOME_NAVIGATION)) || 0;
    // const newState = Math.min(Math.max(currentState + 1, 0), 2);
    // TODO: remove this line in future with feature multiple tab and clean conversation
    const newState = Math.min(Math.max(welcomeNavigationStateChatbot.value + 1, 0), 2);
    // TODO: uncomment in future with feature multiple tab and clean conversation
    // localStorage.setItem(WELCOME_NAVIGATION, newState.toString());
    welcomeNavigationStateChatbot.value = newState;
    welcomeAction(skip);
  }

  const resetChatbot = (e) => {
    e.preventDefault();

    // Reset connection with same uuid with different index
    const index = socketChatbotUuid.split(' - ')[1];
    socketChatbotUuid = generateUuidChatbot(socketChatbotUuid, parseInt(index) + 1);
    socketChatbot.io.opts.query = {token: null, uuid: socketChatbotUuid};
    window.sessionStorage.removeItem(CHATBOT_TOKEN_STRING);

    // Reset all states
    inputValueStateChatbot.value = '';
    welcomeNavigationStateChatbot.value = 0;
    dictMessengersStateChatbot.value = {
      [CHATBOT_MODE_CHATBOT]: [],
      [CHATBOT_MODE_RECOMMENDER]: [],
    };

    // Reconnect socket with new uuid
    socketChatbot.disconnect().connect();
    openCallChatbot(socketChatbot.io.opts.query.uuid);
  };

  const backgroundIsWhite = navigationStateChatbot.value === 0 || (navigationStateChatbot.value === 1
    && welcomeNavigationStateChatbot.value === 0 || welcomeNavigationStateChatbot.value === 1);

  const chatBotBackgroundIsActive = isOpenChatbot.value && backgroundIsWhite;
  const recommenderBackgroundIsActive = isOpenChatbot.value && navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 2;

  return (
    <>
      {chatBotBackgroundIsActive &&
        <div className="bot-utn-mobile-background" />
      }
      {recommenderBackgroundIsActive &&
        <div className="bot-utn-mobile-recommender-background" />
      }
      <div className="bot-utn-down-elements">
        {isOpenChatbot.value &&
          <div className={clsx(
                'bot-utn-card-chat', {
                'bot-utn-recommender-background': navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 2,
              })
            }
          >
            <div
              className="bot-utn-container-content">
              <ChatHeaderChatbot modeChatbot={modeChatbot.value} welcomeNavigationStateChatbot={welcomeNavigationStateChatbot.value} resetChatbotFunc={resetChatbot} welcomeNavigationState={welcomeNavigationStateChatbot} navigationState={navigationStateChatbot} isOpen={isOpenChatbot} />
              {navigationStateChatbot.value === 0 &&
                <>
                  <HistoryChatbot modeChatbot={modeChatbot.value} navigationStateChatbot={navigationStateChatbot.value} messages={dictMessengersStateChatbot.value[modeChatbot.value]} isLoadingState={isLoadingStateDictChatbot.value[modeChatbot.value]}/>
                  <ChatInputFormChatbot sendMessage={sendMessageChatbot} isLoading={(isLoadingStateDictChatbot.value[modeChatbot.value] || !connectedChatbotState)} inputValueState={inputValueStateChatbot}/>
                </>
              }
              {navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 0 &&
                <>
                  <RecommenderSlide sendNavigationChange={sendNavigationChange}/>
                </>
              }
              {navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 1 &&
                <>
                  <ScreenInputLinkedinChatbot sendNavigationChange={sendNavigationChange} sendMessage={sendMessageChatbot}/>
                </>
              }
              {navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 2 &&
                <>
                  <HistoryChatbot welcomeNavigationStateChatbot={welcomeNavigationStateChatbot.value} messages={dictMessengersStateChatbot.value[modeChatbot.value]} isLoadingState={isLoadingStateDictChatbot.value[modeChatbot.value]}/>
                  <ChatInputFormChatbot sendMessage={sendMessageChatbot} isLoading={(isLoadingStateDictChatbot.value[modeChatbot.value] || !connectedChatbotState)} inputValueState={inputValueStateChatbot}/>
                </>
              }
              <ChatBotNavChatbot navigationState={navigationStateChatbot}/>
            </div>
          </div>
        }
          <div className="bot-utn-dropdown-chatbot-div">
              <DropdownChatbot isOpenChatbot={isOpenChatbot.value} handlerClick={handlerClick} isFirstRenderChatbot={isFirstRenderChatbot.value}/>
          </div>
        </div>
      </>
        );
      }
(async function getPortalIdAndRenderChatbot() {
  // get portal id and render chatbot if not in panel
  await new Promise(resolve => setTimeout(resolve, 3000));
  portalIdChatbot.value = await getPortalId();
  const embeddedComponentShadow = document.getElementById('embedded-component').shadowRoot;
  const toRender = embeddedComponentShadow.getElementById('bot-utn-chatbot-utn-app');
  render(<App />, toRender);
})();
